// react
import { useState } from 'react';

// router
import { useParams, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { Act, Benefit, Entity, ExpenseType, Invoice, Transaction } from '../../../@types';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// forms
import { FormUploadDocs } from '../../forms/FormUploadDocs/FormUploadDocs';

// utils
import { formatCurrency } from '../../../utils/currency';

// props
export interface DialogUploadDocsProps {
	act?: Act;
	DialogActionsProps?: Partial<DialogActionsProps>;
	entity?: Entity;
	invoice?: Invoice;
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	transaction?: Transaction;
}

export const DialogUploadDocs = ({
	act,
	DialogActionsProps: dialogActionsProps = {},
	entity,
	invoice,
	isOpen,
	onClose,
	onSuccess,
	transaction,
}: DialogUploadDocsProps) => {
	// hooks
	const { idAct, idEntity } = useParams();
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();

	// state
	const [isLoading, setIsLoading] = useState(false);

	// vars
	const paramDialogContract = searchParams.get('dialogContract');
	const paramDialogInvoice = searchParams.get('dialogInvoice');
	const contractId = paramDialogContract ? Number(paramDialogContract) : undefined;
	const invoiceId = invoice?.id || (paramDialogInvoice ? Number(paramDialogInvoice) : undefined);
	const formId = `form-upload-docs`;

	let subtitle: DialogActionsProps['subtitle'] = dialogActionsProps.subtitle;

	if (!subtitle && act && !idAct) {
		subtitle = {
			children: act.name,
		};
	} else if (!subtitle && entity && !idEntity) {
		subtitle = {
			children: entity.name,
		};
	} else if (!subtitle && invoice) {
		subtitle = {
			children: invoice.num,
		};
	} else if (!subtitle && transaction) {
		const amount = formatCurrency({
			currency: transaction.currency,
			value: transaction.amount,
		});

		if (transaction.benefit && transaction.quantity) {
			subtitle = {
				children: `${transaction.quantity} ${(transaction.benefit as Benefit).name}`,
			};
		} else if (transaction.amount) {
			subtitle = {
				children: amount,
			};

			if (transaction.expenseType) {
				subtitle.children = `${amount} (${(transaction.expenseType as ExpenseType).name})`;
			}
		}
	}

	return (
		<DialogActions
			{...dialogActionsProps}
			actions={[
				{
					children: t('cancel'),
					onClick: onClose,
				},
				{
					children: t('uploadDocuments'),
					form: formId,
					loading: isLoading,
					type: 'submit',
					variant: 'contained',
				},
			]}
			alignItems="center"
			hasClose={true}
			IconCircleProps={{
				type: 'document',
			}}
			isOpen={isOpen}
			onClose={onClose}
			subtitle={subtitle}
			title={{
				children: t('uploadDocuments'),
				...(dialogActionsProps.title || {}),
			}}>
			<FormUploadDocs
				actId={act?.id}
				contractId={contractId}
				entity={entity}
				invoiceId={invoiceId}
				onClose={onClose}
				onSuccess={onSuccess}
				setIsLoading={setIsLoading}
				transaction={transaction}
			/>
		</DialogActions>
	);
};
